// The room's settings, shared by the plain call (call.js) and the Canvas session (session.js) so a phone is treated
// the same in both (Ravi, 28 Sep: the video on a phone is soft; a dropped connection had to be joined again by hand).
import { VideoPresets } from "livekit-client";

// How long the room itself keeps trying after the connection drops, before the page takes over (main.js rejoins with a
// fresh pass for as long again). LiveKit's own policy gave up after ~35 s — shorter than a lift, a tunnel, or a phone
// switching from Wi-Fi to 5G — and the person then had to join again by hand.
export const RECONNECT_FOR_MS = 5 * 60_000;

// The wait before each retry: at once, then quickly, then every two seconds for the first minute and every five after
// that, until RECONNECT_FOR_MS is up (null: give up). Pure; tested.
export function nextRetryDelay({ elapsedMs = 0, retryCount = 0 } = {}) {
  if (elapsedMs >= RECONNECT_FOR_MS) return null;
  if (retryCount < 4) return [0, 300, 800, 1500][retryCount];
  return elapsedMs < 60_000 ? 2000 : 5000;
}

export const AUDIO_CAPTURE = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
export const CAMERA = { resolution: VideoPresets.h720.resolution };

// What every Room is made with.
//  · adaptiveStream on the screen's own pixel density: a phone with a 3× screen asks for the layer that fills its
//    pixels, not a 2× guess (the strip's faces were coming in at 180p).
//  · degradationPreference "maintain-resolution": when a phone's CPU or uplink cannot keep up, LiveKit's default for a
//    camera ("maintain-framerate") lowered the picture to 360p or less and kept 30 fps — a soft face. Zoom's choice is
//    the opposite: keep the picture sharp and let the frame rate fall, which a talking head hardly shows.
//  · the camera's top layer at 2.5 Mbps whatever its size (LiveKit's 720p default is 1.7; a smaller camera rarely
//    reaches it and is not hurt by it): most people are on 5G now (Ravi). A screen share has its own encoding.
//  · reconnectPolicy: five minutes of retries, above.
export function roomOptions(joined = {}) {
  return {
    adaptiveStream: { pixelDensity: "screen" },
    dynacast: true,
    reconnectPolicy: { nextRetryDelayInMs: nextRetryDelay },
    videoCaptureDefaults: CAMERA,
    publishDefaults: {
      simulcast: true,
      videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
      videoEncoding: { maxBitrate: 2_500_000, maxFramerate: 30 },
      degradationPreference: "maintain-resolution",
      red: true,
      dtx: true,
    },
    audioCaptureDefaults: AUDIO_CAPTURE,
    ...(joined.audioOutput ? { audioOutput: { deviceId: joined.audioOutput } } : {}),
  };
}

// What an automatic rejoin opens again (main.js reopenDevices), decided from how the person LAST had things in the
// call — not from what the join page opened (re-audit 28 Sep): a camera turned off in the call stays off (no light, no
// track to stop), one turned on in the call after a blocked start comes back on, and the devices switched to mid-call
// are the ones opened. Pure; tested. `joined`: the join page's set; `last`: the call's { micOn, camOn, audio, video }.
export function reopenPlan(joined = {}, last = null) {
  const micOn = last ? Boolean(last.micOn) : Boolean(joined.micOn && joined.audioTrack);
  const camOn = last ? Boolean(last.camOn) : Boolean(joined.camOn && joined.videoTrack);
  const d = joined.devices || {};
  return {
    audio: micOn, video: camOn,
    state: { micOn, camOn },
    devices: { audio: (last && last.audio) || d.audio || "", video: (last && last.video) || d.video || "" },
  };
}
